import { useMemo, useRef, useState } from "react";

import { Check, ChevronDown, Loader2, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { EndpointDef, PermissionGroupDef } from "@/features/users/data/endpoints";
import { useEndpointsConfig } from "@/features/users/hooks/useEndpointsConfig";
import { usePermissionGroups } from "@/features/users/hooks/usePermissionGroups";
import {
  isGroupFullySelected,
  pathsForGroup,
  toggleIdsInSelection,
} from "@/features/users/utils/pathsForGroup";
import { cn } from "@/shared/lib/utils";

/**
 * Picker de endpoints + atajos por grupo.
 *
 * - Chips de grupos fuera del popover: un click aplica los paths del grupo.
 * - Catálogo desde Firestore (`api_permissions` / `api_permission_groups`).
 * - La selección y el payload de API key usan paths HTTP (`EndpointDef.id`).
 */

const MAX_VISIBLE_CHIPS = 5;

interface EndpointCommandPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /**
   * Si true, solo muestra resumen + botón; el catálogo/grupos se expanden
   * al pedir detalle (evita romper el modal con muchos endpoints).
   */
  compact?: boolean;
}

type AuthFilter = "all" | "apiKey" | "none";

const FILTER_OPTIONS: { value: AuthFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "apiKey", label: "Con auth" },
  { value: "none", label: "Sin auth" },
];

const METHOD_BADGE: Record<string, string> = {
  POST: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  GET: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  PUT: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
  DELETE: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

function groupByCategory(endpoints: EndpointDef[]): Record<string, EndpointDef[]> {
  const groups: Record<string, EndpointDef[]> = {};
  for (const ep of endpoints) {
    if (!groups[ep.category]) groups[ep.category] = [];
    groups[ep.category].push(ep);
  }
  return groups;
}

export function EndpointCommandPicker({
  value,
  onChange,
  placeholder = "Seleccioná endpoints…",
  compact = false,
}: EndpointCommandPickerProps) {
  const { data: endpoints = [], isLoading: isLoadingEndpoints, isError: isEndpointsError } =
    useEndpointsConfig();
  const { data: permissionGroups = [], isLoading: isLoadingGroups } = usePermissionGroups();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [authFilter, setAuthFilter] = useState<AuthFilter>("all");
  const [detailOpen, setDetailOpen] = useState(!compact);
  const listWrapperRef = useRef<HTMLDivElement>(null);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const permissionsByDocId = useMemo(() => {
    const map = new Map<string, EndpointDef>();
    for (const ep of endpoints) map.set(ep.docId, ep);
    return map;
  }, [endpoints]);

  const endpointByPath = useMemo(() => {
    const map = new Map<string, EndpointDef>();
    for (const ep of endpoints) map.set(ep.id, ep);
    return map;
  }, [endpoints]);

  const groupPathsById = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const group of permissionGroups) {
      map.set(group.docId, pathsForGroup(group, permissionsByDocId));
    }
    return map;
  }, [permissionGroups, permissionsByDocId]);

  const selectedEndpoints = useMemo(() => {
    return value
      .map((id) => endpointByPath.get(id))
      .filter((ep): ep is EndpointDef => Boolean(ep));
  }, [value, endpointByPath]);

  const visibleChips = selectedEndpoints.slice(0, MAX_VISIBLE_CHIPS);
  const overflowCount = selectedEndpoints.length - visibleChips.length;

  const filteredGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = endpoints.filter((ep) => {
      if (authFilter !== "all" && ep.auth !== authFilter) return false;
      if (!q) return true;
      return (
        ep.label.toLowerCase().includes(q) ||
        ep.id.toLowerCase().includes(q) ||
        ep.category.toLowerCase().includes(q)
      );
    });
    return groupByCategory(matches);
  }, [endpoints, query, authFilter]);

  const hasResults = Object.values(filteredGroups).some((eps) => eps.length > 0);

  const visibleIds = useMemo(() => {
    const ids: string[] = [];
    for (const group of Object.values(filteredGroups)) {
      for (const ep of group) ids.push(ep.id);
    }
    return ids;
  }, [filteredGroups]);

  const allVisibleSelected = useMemo(() => {
    if (visibleIds.length === 0) return false;
    for (const id of visibleIds) {
      if (!selectedSet.has(id)) return false;
    }
    return true;
  }, [visibleIds, selectedSet]);

  const toggleEndpoint = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((existing) => existing !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const clearAll = () => onChange([]);
  const close = () => setOpen(false);

  const bulkToggleVisible = (ids: string[]) => {
    onChange(toggleIdsInSelection(value, ids));
  };

  const applyGroup = (group: PermissionGroupDef) => {
    const paths = groupPathsById.get(group.docId) ?? [];
    onChange(toggleIdsInSelection(value, paths));
  };

  const isLoading = isLoadingEndpoints || isLoadingGroups;

  if (compact && !detailOpen) {
    return (
      <div className="space-y-2 rounded-md border border-dashed border-border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium">
              {value.length === 0
                ? "Ningún endpoint seleccionado"
                : `${value.length} endpoint(s) seleccionado(s)`}
            </p>
            {value.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {visibleChips.map((ep) => (
                  <Badge key={ep.id} variant="secondary" className="font-normal" title={ep.id}>
                    {ep.label}
                  </Badge>
                ))}
                {overflowCount > 0 && (
                  <Badge variant="secondary" className="font-normal">
                    +{overflowCount}
                  </Badge>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setDetailOpen(true)}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1.5 text-xs font-medium hover:bg-accent"
          >
            Elegir endpoints…
            <ChevronDown className="size-3.5 opacity-60" />
          </button>
        </div>
        {isLoading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            Cargando permisos…
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {compact && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => {
              setDetailOpen(false);
              setOpen(false);
            }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Minimizar detalle
          </button>
        </div>
      )}
      {/* Atajos de grupo: visibles sin abrir el popover */}
      {permissionGroups.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-medium text-muted-foreground">Grupos rápidos</p>
          <div className="flex max-h-20 flex-wrap gap-1.5 overflow-y-auto">
            {permissionGroups.map((group) => {
              const paths = groupPathsById.get(group.docId) ?? [];
              const active = isGroupFullySelected(paths, selectedSet);
              const disabled = paths.length === 0;
              return (
                <button
                  key={group.docId}
                  type="button"
                  disabled={disabled}
                  title={
                    disabled
                      ? "Este grupo no tiene permisos resueltos"
                      : (group.description ?? `${paths.length} endpoint(s)`)
                  }
                  onClick={() => applyGroup(group)}
                  className={cn(
                    "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
                    active
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input bg-background hover:bg-accent hover:text-accent-foreground",
                    disabled && "pointer-events-none opacity-50"
                  )}
                >
                  {group.label}
                  <span className="ml-1 opacity-70">({paths.length})</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Cargando permisos…
        </div>
      )}

      {isEndpointsError && (
        <p className="text-xs text-destructive">
          No se pudo cargar el catálogo de permisos desde Firebase.
        </p>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Seleccionar endpoints"
            disabled={isLoadingEndpoints}
            className={cn(
              "flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-3 py-1.5 text-left text-sm shadow-xs",
              "hover:bg-accent/30 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-hidden",
              "disabled:pointer-events-none disabled:opacity-50"
            )}
          >
            {selectedEndpoints.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              <>
                {visibleChips.map((ep) => (
                  <Badge
                    key={ep.id}
                    variant="secondary"
                    className="font-normal"
                    title={ep.id}
                  >
                    {ep.label}
                  </Badge>
                ))}
                {overflowCount > 0 && (
                  <Badge variant="secondary" className="font-normal">
                    +{overflowCount}
                  </Badge>
                )}
                <ChevronDown className="ml-auto size-4 opacity-50" />
              </>
            )}
            {selectedEndpoints.length === 0 && (
              <ChevronDown className="ml-auto size-4 opacity-50" />
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-[640px] max-w-[calc(100vw-2rem)] p-0"
        >
          <div className="flex max-h-[min(16rem,calc(100vh-8rem))] flex-col">
            <div className="flex items-center gap-1.5 border-b px-3 py-2">
              {FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAuthFilter(opt.value)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                    authFilter === opt.value
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input bg-background hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  {opt.label}
                </button>
              ))}
              <span className="ml-auto" />
              <button
                type="button"
                onClick={() => bulkToggleVisible(visibleIds)}
                disabled={visibleIds.length === 0}
                className="rounded-md border border-input bg-background px-2 py-0.5 text-xs hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
                title="Selecciona o deselecciona todos los endpoints visibles"
              >
                {allVisibleSelected ? "Deseleccionar visibles" : "Seleccionar visibles"}
              </button>
            </div>

            <Command shouldFilter={false} className="min-h-0 flex-1 overflow-hidden">
              <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder="Buscar endpoint..."
              />
              {query && (
                <div className="flex justify-end border-b px-3 py-1">
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Limpiar búsqueda"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              )}

              <div
                ref={listWrapperRef}
                onWheel={(e) => {
                  const target = e.currentTarget;
                  const scrollable = target.querySelector<HTMLElement>("[cmdk-list]");
                  const el = scrollable ?? target;
                  if (el.scrollHeight > el.clientHeight) {
                    el.scrollTop += e.deltaY;
                    e.preventDefault();
                  }
                }}
                className="max-h-[480px] overflow-y-auto"
              >
                <CommandList className="max-h-[480px] overflow-y-auto">
                  {!hasResults ? (
                    <CommandEmpty>
                      {endpoints.length === 0
                        ? "No hay permisos en Firebase (colección api_permissions)."
                        : "¿No encontrás el endpoint? Pedí que lo agreguemos al catálogo."}
                    </CommandEmpty>
                  ) : (
                    Object.entries(filteredGroups).map(([category, categoryEndpoints]) => {
                      const categoryIds = categoryEndpoints.map((ep) => ep.id);
                      const categoryAllSelected = categoryIds.every((id) =>
                        selectedSet.has(id)
                      );
                      return (
                        <CommandGroup key={category} heading={category}>
                          <div className="mb-1 ml-6 mr-2 flex justify-end">
                            <button
                              type="button"
                              onClick={() => bulkToggleVisible(categoryIds)}
                              className="rounded px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            >
                              {categoryAllSelected
                                ? `Deseleccionar ${categoryEndpoints.length}`
                                : `Seleccionar ${categoryEndpoints.length}`}
                            </button>
                          </div>
                          {categoryEndpoints.map((ep) => {
                            const isSelected = selectedSet.has(ep.id);
                            return (
                              <CommandItem
                                key={ep.docId}
                                value={ep.id}
                                onSelect={() => toggleEndpoint(ep.id)}
                                className="flex items-start gap-2 py-2"
                              >
                                <span
                                  className={cn(
                                    "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-sm border",
                                    isSelected
                                      ? "border-primary bg-primary text-primary-foreground"
                                      : "border-input"
                                  )}
                                  aria-hidden
                                >
                                  {isSelected && <Check className="size-3" />}
                                </span>
                                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={cn(
                                        "shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px]",
                                        METHOD_BADGE[ep.method] ??
                                          "bg-gray-100 text-gray-600"
                                      )}
                                    >
                                      {ep.method}
                                    </span>
                                    <span className="truncate font-mono text-xs text-muted-foreground">
                                      {ep.id}
                                    </span>
                                    {ep.auth === "none" && (
                                      <Badge
                                        variant="outline"
                                        className="shrink-0 border-amber-500 text-[10px] text-amber-600"
                                        title="Este endpoint no requiere autenticación"
                                      >
                                        sin auth
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className="truncate text-sm font-medium">
                                      {ep.label}
                                    </span>
                                    {ep.description && (
                                      <span className="truncate text-xs text-muted-foreground">
                                        {ep.description}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      );
                    })
                  )}
                </CommandList>
              </div>
            </Command>

            <div className="flex items-center justify-between gap-2 border-t px-3 py-2 text-xs">
              <span className="text-muted-foreground">
                {value.length} seleccionado{value.length === 1 ? "" : "s"}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={clearAll}
                  disabled={value.length === 0}
                  className="rounded px-2 py-1 text-xs hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
                >
                  Limpiar
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground hover:bg-primary/90"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
